import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import MuiInputBase from '@material-ui/core/InputBase';
import MuiPaper from '@material-ui/core/Paper';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTable from '@material-ui/core/Table';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiToolbar from '@material-ui/core/Toolbar';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  Box,
  IconButton,
  withStyles,
  fade,
  Tooltip,
} from '@material-ui/core';
import { HelpRounded } from '@material-ui/icons';
import SearchIcon from '@material-ui/icons/Search';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import { getLinkQueryParamUrl, QueryValUrl } from 'Helpers/Query';
import { SORT } from 'Consts/queryParams';
import Button from 'Components/UI/Button';
import Spacer from 'Components/UI/Spacer';
import useDebounce from 'Hooks/useDebounce';
import { parseQueriesFromCollection } from 'Helpers/Meta';
import actions from './actions';
import CreateDialog from './CreateDialog';
import EditDialog from './EditDialog';
import TableCell from './TableCell';
import TableRow from './TableRow';


import { parseActionQueryParam, setActionQueryParam } from './utils';

const InfoIcon = withStyles(theme => ({
  root: {
    color: theme.palette.facet.accentBlue,
    paddingLeft: theme.spacing(1),
  },
}))(HelpRounded);

const styles = theme => ({
  root: {},
  table: {},
  head: {},
  body: {},
  row: {},
  cell: {},
  cellContent: {},
  toolbar: {
    justifyContent: 'flex-end',
  },
  createButtonIcon: {
    marginRight: theme.spacing(1),
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.facet.lightGray}`,
    backgroundColor: fade(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.facet.lightGray, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: theme.dimensions.lg,
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.facet.accentGreen,
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
    fontSize: theme.typography.h6.fontSize,
    width: '100%',
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
  tableNavigation: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(3),
  },
});

function showToolbar(features) {
  return !!features.create || !!features.search;
}

function Table({
  actions: actionsProp,
  className,
  classes,
  collection,
  columns,
  features,
  filter,
  onSortUpdate,
  linkQueryParamName,
  onClickPaginationLink,
  ContainerProps,
  BodyProps,
  CellProps,
  HeadProps,
  HeadRowProps,
  RowProps,
  TableProps,
  disablePagination,
  onExpanded,
}) {
  const history = useHistory();
  const actionParam = parseActionQueryParam();
  const resources = (filter) ? collection.resources().list().filter(filter) : collection.resources().list();
  const closeDialog = () => {
    setActionQueryParam(history, null);
  };
  const debouncedOnChange = useDebounce(args => features.search.onSubmit(args), 500);

  const onPrevPage = async () => {
    onClickPaginationLink(collection.link('prev'));
  };

  const onNextPage = () => {
    onClickPaginationLink(collection.link('next'));
  };

  const queries = parseQueriesFromCollection(collection);
  return (
    <>
      <MuiTableContainer
        className={classNames(classes.root, className)}
        component={MuiPaper}
        elevation={0}
        {...ContainerProps}
      >
        {showToolbar(features) && (
          <MuiToolbar
            className={classes.toolbar}
            disableGutters
          >
            {(features.search) ? (
              <div className={classes.search}>
                <div className={classes.searchIcon}>
                  <SearchIcon />
                </div>
                <MuiInputBase
                  placeholder={features.search.title || 'Search...'}
                  classes={{
                    root: classes.inputRoot,
                    input: classes.inputInput,
                  }}
                  variant="outlined"
                  inputProps={{ 'aria-label': 'search' }}
                  onChange={(event) => {
                    debouncedOnChange({ collection, query: event.target.value });
                  }}
                />
              </div>
            ) : null}
            <Spacer />
            {(features.create) ? (
              <Button
                color="secondary"
                onClick={() => {
                  setActionQueryParam(history, 'create', {
                    metaOverrideSetName: features.create.metaOverrideSetName,
                  });
                }}
              >
                <AddCircleIcon
                  className={classes.createButtonIcon}
                />
                {features.create.button}
              </Button>
            ) : null}
          </MuiToolbar>
        )}
        <MuiTable
          className={classes.table}
          {...TableProps}
        >
          {/* This is somewhere you might come to add a colgroup. It is garbage. Apply styles to td's instead. */}
          <MuiTableHead
            className={classes.head}
            {...HeadProps}
          >
            <MuiTableRow
              className={classes.row}
              {...HeadRowProps}
            >
              {features?.edit?.type === 'hybrid' && (
                <TableCell
                  className={classes.cell}
                  key="edit"
                  {...CellProps}
                  style={{
                    ...CellProps.style,
                  }}
                >
                  <div />
                </TableCell>
              )}
              {onExpanded && (
              <TableCell
                className={classes.cell}
                key="expand"
                {...CellProps}
                style={{
                  ...CellProps.style,
                }}
              >
                <div />
              </TableCell>
              )}
              {/* eslint-disable-next-line arrow-body-style */}
              {columns.map((column) => {
                const isAction = !!column.action;
                const hideLabel = column.hideHeader || isAction || !column.name;
                const label = hideLabel ? '' : collection.meta().field(column.name).attr('prompt');
                let columnAction = {};
                if (isAction) {
                  const actionName = column.action.name;
                  if (!(actionName in actions) && !(actionName in actionsProp)) {
                    throw new Error(`Action ${actionName} does not exist`);
                  }
                  columnAction = actionsProp[actionName] || actions[actionName];
                }
                if (column.infoTooltip) {
                  const tooltip = column.infoTooltip.component ? column.infoTooltip.component : (
                    <Tooltip {...column.infoTooltip}>
                      <InfoIcon />
                    </Tooltip>
                  );

                  return (
                    <TableCell
                      className={classes.cell}
                      colSpan={column.span}
                      key={(isAction) ? columnAction.columnName : column.name}
                      {...CellProps}
                      style={{
                        ...CellProps.style,
                        ...column.style, // column style gets the last laugh on styling
                      }}
                    >
                      {isAction
                        ? columnAction.columnName
                        : collection.meta().field(column.name).attr('prompt')}
                      {tooltip}
                    </TableCell>
                  );
                }


                const isSortable = onSortUpdate && linkQueryParamName && queries[column.name] && queries[column.name].attr('sortable');

                const descendingName = `-${column.name}`;
                const ascendingName = column.name;

                let sortDirection = null;
                if (isSortable) {
                  const linkQueryParamUrl = getLinkQueryParamUrl(collection, linkQueryParamName);
                  if (linkQueryParamUrl) {
                    const sortParam = QueryValUrl(linkQueryParamUrl, SORT);
                    if (sortParam && sortParam === ascendingName) {
                      sortDirection = 'asc';
                    } else if (sortParam && sortParam === descendingName) {
                      sortDirection = 'desc';
                    }
                  }
                }

                const onSortClick = isSortable
                  ? () => {
                    const linkQueryParamUrl = getLinkQueryParamUrl(collection, linkQueryParamName);
                    if (linkQueryParamUrl) {
                      const sortParam = QueryValUrl(linkQueryParamUrl, SORT);
                      if (sortParam && sortParam === ascendingName) {
                        sortDirection = 'desc';
                        linkQueryParamUrl.searchParams.set(SORT, descendingName);
                      } else if (sortParam && sortParam === descendingName) {
                        sortDirection = null;
                        linkQueryParamUrl.searchParams.delete(SORT);
                      } else {
                        sortDirection = 'asc';
                        linkQueryParamUrl.searchParams.set(SORT, ascendingName);
                      }
                      onSortUpdate(linkQueryParamUrl.toString());
                    }
                  }
                  : () => {};

                let carrot = null;
                if (sortDirection === 'asc') {
                  carrot = <ArrowDropUpIcon />;
                } else if (sortDirection === 'desc') {
                  carrot = <ArrowDropDownIcon />;
                } else {
                  carrot = <UnfoldMoreIcon />;
                }

                return (
                  <TableCell
                    className={classes.cell}
                    colSpan={column.span}
                    key={(isAction) ? column.action.columnName : column.name}
                    {...CellProps}
                    style={{
                      ...CellProps.style,
                      ...column.style, // column style gets the last laugh on styling
                    }}
                  >
                    <div>
                      {isAction ? columnAction.columnName : label}
                      {isSortable ? <IconButton size="small" onClick={onSortClick}>{carrot}</IconButton> : null}
                    </div>
                  </TableCell>
                );
              })}
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody
            className={classes.body}
            {...BodyProps}
          >
            {resources.map((resource) => {
              const rowClickable = features?.edit?.type === 'dialog';
              const onDelete = (features?.delete?.type === 'dialog' || features?.delete?.type === 'hybrid') ? features?.delete?.onSubmit : null;
              const onSubmit = features?.edit?.onSubmit;
              const onClick = features?.onClick;
              const isSelected = features?.isSelected && typeof features?.isSelected === 'function' ? features?.isSelected(resource) : false;
              return (
                <React.Fragment key={`${resource.id()}-tr`}>
                  <TableRow
                    onExpanded={onExpanded}
                    columns={columns}
                    inlineEditable={features?.edit?.type === 'inline' || features?.edit?.type === 'hybrid'}
                    onSubmit={onSubmit}
                    resource={resource}
                    onClick={(rowClickable || onClick) ? () => {
                      if (rowClickable) {
                        setActionQueryParam(history, 'edit', {
                          metaOverrideSetName: features.edit.metaOverrideSetName,
                          resource,
                        });
                      }
                      if (onClick) {
                        onClick(resource);
                      }
                    } : null}
                    onEditButtonClick={features?.edit?.type === 'hybrid' ? () => {
                      setActionQueryParam(history, 'edit', {
                        metaOverrideSetName: features.edit.metaOverrideSetName,
                        resource,
                      });
                    } : null}
                    CellProps={CellProps}
                    RowProps={RowProps}
                    actions={actionsProp}
                    test="meta-table-row"
                    selected={isSelected}

                  />
                  {actionParam.actionName === 'edit' && resource.link('self').url() === actionParam.url && (
                    <EditDialog
                      collection={collection}
                      onClose={closeDialog}
                      resourceURL={actionParam.url}
                      onDelete={onDelete}
                      {...features.edit}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </MuiTableBody>
        </MuiTable>
        {!disablePagination && onClickPaginationLink && (
          <Box className={classes.tableNavigation}>
            <Button
              color="secondary"
              disabled={!collection.link('prev')}
              onClick={onPrevPage}
            >
              <ChevronLeftIcon /> Previous
            </Button>
            <Button
              color="secondary"
              disabled={!collection.link('next')}
              onClick={onNextPage}
            >
              Next <ChevronRightIcon />
            </Button>
          </Box>
        )}
      </MuiTableContainer>
      {actionParam.actionName === 'create' && (
        <CreateDialog
          collection={collection}
          onClose={closeDialog}
          {...features.create}
        />
      )}
    </>
  );
}

Table.defaultProps = {
  className: '',
  features: {},
  actions: {},
  filter: null,
  onSortUpdate: null,
  linkQueryParamName: 'link',
  BodyProps: {},
  CellProps: {},
  HeadProps: {},
  HeadRowProps: {},
  RowProps: {},
  TableProps: {},
  rowsPerPage: null,
  disablePagination: false,
};
Table.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  collection: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    minWidth: PropTypes.string,
    align: PropTypes.oneOf(['left', 'right']),
    format: PropTypes.func,
    action: PropTypes.object,
    hideHeader: PropTypes.bool,
  })).isRequired,
  features: PropTypes.shape({
    create: PropTypes.shape({
      type: PropTypes.oneOf(['dialog']),
      metaOverrideSet: PropTypes.object,
      onSubmit: PropTypes.func,
      onFailure: PropTypes.func,
    }),
    edit: PropTypes.shape({
      type: PropTypes.oneOf(['inline', 'dialog', 'hybrid']),
      metaOverrideSet: PropTypes.object,
      onSubmit: PropTypes.func,
    }),
  }),
  filter: PropTypes.func,
  onSortUpdate: PropTypes.func,
  onRowClick: PropTypes.func,
  rowsPerPage: PropTypes.number,
  linkQueryParamName: PropTypes.string,
  BodyProps: PropTypes.object,
  CellProps: PropTypes.object,
  HeadProps: PropTypes.object,
  HeadRowProps: PropTypes.object,
  RowProps: PropTypes.object,
  TableProps: PropTypes.object,
  disablePagination: PropTypes.bool,
  onExpanded: PropTypes.func,
};
export default withStyles(styles)(Table);
